// src/services/workbookService.js
// ================================================================
// سرویس کتاب کار — نسخه نهایی
// ----------------------------------------------------------------
// • چک لایسنس از localStorage (سریع و مطمئن) + سرور (پشتیبان)
// • فعال‌سازی: اول localStorage، بعد سرور
// • ذخیره یادداشت‌ها در سرور
// ================================================================

// ✅ تغییر یافت: اتصال به دروازه‌بان
const API_BASE = '/api/db';
const UPLOAD_BASE = process.env.REACT_APP_UPLOAD_URL || '/upload';

// ================================================================
// توابع کمکی
// ================================================================

/** چک اینکه یک لایسنس هنوز معتبر است */
const isLicenseValid = (license) => {
  if (!license || !license.expiresAt) return false;
  try {
    return new Date(license.expiresAt).getTime() > Date.now();
  } catch {
    return false;
  }
};

// ⚠️ localStorage حذف شد — لایسنس‌ها فقط در سرور (/workbookLicenses) ذخیره می‌شوند.

// ================================================================
// سرویس اصلی
// ================================================================
export const workbookService = {

  // ============================================================
  // ۱. بررسی لایسنس
  // ------------------------------------------------------------
  // اولویت:
  //   ۱. localStorage (اگر معتبر بود → برگردون)
  //   ۲. سرور (اگر موجود بود → کش کن و برگردون)
  // ============================================================
  async checkLicense(userId, bookId) {
    if (!userId || !bookId) return null;

    // فقط از سرور بخوان
    try {
      const res = await fetch(
        `${API_BASE}/workbookLicenses?userId=${encodeURIComponent(userId)}&bookId=${encodeURIComponent(bookId)}`
      );
      if (!res.ok) return null;
      const licenses = await res.json();
      if (!Array.isArray(licenses) || licenses.length === 0) return null;

      // آخرین لایسنس (بزرگ‌ترین expiresAt)
      const sorted = [...licenses].sort(
        (a, b) => new Date(b.expiresAt || 0) - new Date(a.expiresAt || 0)
      );
      const license = sorted[0];

      if (isLicenseValid(license)) {
        return license;
      }

      // منقضی
      return { ...license, expired: true };
    } catch {
      return null;
    }
  },

  // ============================================================
  // ۲. فعال‌سازی لایسنس (پس از پرداخت موفق)
  // ------------------------------------------------------------
  // ۱. اول localStorage (تضمین دسترسی فوری)
  // ۲. بعد سرور (پاک کردن رکوردهای قدیمی + ساخت جدید)
  // ============================================================
  async activateLicense(userId, bookId, paymentId, durationDays = 365) {
    if (!userId || !bookId) {
      throw new Error('userId و bookId الزامی است');
    }

    const now = new Date();
    const expiresAt = new Date(
      now.getTime() + durationDays * 24 * 60 * 60 * 1000
    );

    const newLicense = {
      id: `lic_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      userId,
      bookId,
      paymentId: paymentId || `pay_${Date.now()}`,
      purchasedAt: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
      status: 'active',
    };

    // ============================================================
    // ذخیره در سرور (منبع اصلی)
    // ============================================================
    try {
      // ۱. پاک کردن همه رکوردهای قبلی این کاربر/کتاب
      const existingRes = await fetch(
        `${API_BASE}/workbookLicenses?userId=${encodeURIComponent(userId)}&bookId=${encodeURIComponent(bookId)}`
      );

      if (existingRes.ok) {
        const existing = await existingRes.json();
        if (Array.isArray(existing) && existing.length > 0) {
          await Promise.all(
            existing.map((rec) =>
              fetch(`${API_BASE}/workbookLicenses/${rec.id}`, {
                method: 'DELETE',
              }).catch(() => {})
            )
          );
          console.log(`🗑️ ${existing.length} رکورد قدیمی پاک شد`);
        }
      }

      // ۲. ساخت رکورد جدید
      const postRes = await fetch(`${API_BASE}/workbookLicenses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newLicense),
      });

      if (postRes.ok) {
        const savedServer = await postRes.json();
        console.log('✅ لایسنس در سرور ذخیره شد:', savedServer.id);
        return savedServer;
      } else {
        console.warn('⚠️ سرور لایسنس را نپذیرفت (کد:', postRes.status, ')');
      }
    } catch (e) {
      console.warn('⚠️ خطا در ارتباط با سرور:', e);
    }

    // اگر سرور خطا داد، فقط اطلاعات را برگردان (فقط UI موقت)
    return newLicense;
  },

  // ============================================================
  // ۳. دریافت session امن برای رمزگشایی PDF
  // ============================================================
  async getSecureSession(userId, bookId) {
    const res = await fetch(`${UPLOAD_BASE}/api/secure-pdf/init`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, bookId }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'خطا در دریافت دسترسی امن');
    }
    return res.json();
  },

  // ============================================================
  // ۴. ذخیره یادداشت‌ها
  // ============================================================
  async saveAnnotations(userId, bookId, pageNumber, strokes) {
    if (!userId || !bookId || !pageNumber) return;

    try {
      const existingRes = await fetch(
        `${API_BASE}/workbookAnnotations?userId=${encodeURIComponent(userId)}&bookId=${encodeURIComponent(bookId)}&pageNumber=${pageNumber}`
      );
      const existing = existingRes.ok ? await existingRes.json() : [];

      const payload = {
        userId,
        bookId,
        pageNumber,
        strokes: strokes || [],
        lastModified: new Date().toISOString(),
        version: ((existing[0]?.version) || 0) + 1,
      };

      if (Array.isArray(existing) && existing.length > 0) {
        // PUT
        await fetch(`${API_BASE}/workbookAnnotations/${existing[0].id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...existing[0], ...payload }),
        });
      } else {
        // POST
        await fetch(`${API_BASE}/workbookAnnotations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: `ann_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
            ...payload,
          }),
        });
      }
    } catch (e) {
      console.warn('⚠️ خطا در ذخیره یادداشت‌ها:', e);
    }
  },

  // ============================================================
  // ۵. بارگذاری یادداشت‌ها
  // ============================================================
  async loadAnnotations(userId, bookId) {
    if (!userId || !bookId) return [];
    try {
      const res = await fetch(
        `${API_BASE}/workbookAnnotations?userId=${encodeURIComponent(userId)}&bookId=${encodeURIComponent(bookId)}`
      );
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  },

  // ============================================================
  // ۶. دریافت همه لایسنس‌های معتبر کاربر
  // ============================================================
  async getUserLicenses(userId) {
    if (!userId) return [];

    const validLicenses = [];
    const seenBookIds = new Set();

    // فقط از سرور بخوان
    try {
      const res = await fetch(
        `${API_BASE}/workbookLicenses?userId=${encodeURIComponent(userId)}`
      );
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          // مرتب بر اساس expiresAt (نزولی) برای گرفتن جدیدترین
          const sorted = [...data].sort(
            (a, b) => new Date(b.expiresAt || 0) - new Date(a.expiresAt || 0)
          );
          for (const lic of sorted) {
            if (!lic.bookId || seenBookIds.has(lic.bookId)) continue;
            if (isLicenseValid(lic)) {
              seenBookIds.add(lic.bookId);
              validLicenses.push(lic);
            }
          }
        }
      }
    } catch {}

    return validLicenses;
  },

  // ============================================================
  // ۷. پاک کردن لایسنس (برای تست/دیباگ)
  // ============================================================
  async clearLicense(userId, bookId) {
    try {
      const res = await fetch(
        `${API_BASE}/workbookLicenses?userId=${encodeURIComponent(userId)}&bookId=${encodeURIComponent(bookId)}`
      );
      if (res.ok) {
        const existing = await res.json();
        if (Array.isArray(existing)) {
          await Promise.all(
            existing.map((rec) =>
              fetch(`${API_BASE}/workbookLicenses/${rec.id}`, {
                method: 'DELETE',
              }).catch(() => {})
            )
          );
        }
      }
    } catch {}
  },
};

export default workbookService;